/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import axiosConfig, { ApiRequestType } from 'axiosConfig/axiosConfig';
import { getDefaultStore } from 'jotai';
import { activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import { EntriesDataResponse, LapData, OriginType, RunType, SanctioningBodyEnum } from 'models/viewModels';
import { StreamRequestType } from './RSocketObject';
import { RSocketStreamBaseService } from './RSocketStreamBaseService';

const defaultStore = getDefaultStore();
class LapDataService extends RSocketStreamBaseService<LapData[], LapData> {
  private cacheEntries: LapData[] = [];
  protected async getStreamHistoryRequest(): Promise<LapData[]> {
    const sanctioningBody = defaultStore.get(activeSanctioningBodyIdAtom);
    if (sanctioningBody === SanctioningBodyEnum.NASCAR) {
      if (this.cacheEntries.length === 0 && this.request?.runType === RunType.PRACTICE) {
        this.cacheEntries = await axiosConfig(ApiRequestType.MotorsportsCompToolsApi)
          .get('/rest/nascar/api/getPracticeEntriesData', {
            params: { seriesId: this.request.seriesType, eventId: this.request.raceId },
          })
          .then((response) => {
            return (response.data as EntriesDataResponse).entriesData;
          })
          .catch(() => {
            return [];
          });
      }
    }
    return axiosConfig(ApiRequestType.StreamMetricsApi)
      .post('/rest/backfill/lap', this.request)
      .then((response) => {
        const backfillResponse = response.data as LapData[];
        return this.request.runType !== RunType.PRACTICE
          ? backfillResponse
          : backfillResponse.length === 0
          ? this.cacheEntries
          : this.cacheEntries.length === 0
          ? backfillResponse
          : this.cacheEntries.map((entry) => {
              const backfillEntry = backfillResponse.find((item) => item.vehicleNumber === entry.vehicleNumber);
              return {
                ...entry,
                ...backfillEntry,
                runName: entry.runName,
              };
            });
      })
      .catch(() => {
        return [];
      });
  }

  protected onDataCallback = async (response: LapData) => {
    const lapData = response;
    if (this.listeners.size > 0) {
      const sanctioningBody = defaultStore.get(activeSanctioningBodyIdAtom);
      if (sanctioningBody === SanctioningBodyEnum.NASCAR) {
        const prevLapData = this.localCache;
        const newLapData = prevLapData.filter((item) => item.vehicleNumber !== lapData.vehicleNumber);
        newLapData.push(lapData);
        this.localCache =
          this.request.runType !== RunType.PRACTICE
            ? newLapData
            : newLapData.map((lapData) => {
                return {
                  ...lapData,
                  runName:
                    this.cacheEntries.find((entry) => entry.vehicleNumber === lapData.vehicleNumber)?.runName || '',
                };
              });
      } else {
        const prevLapData = this.localCache;
        const newLapData = prevLapData.filter((item) => item.vehicleNumber !== lapData.vehicleNumber);
        newLapData.push(lapData);
        this.localCache = newLapData;
      }
    }
  };

  async start() {
    this.delay = 1000;
    super.start();
  }
}

export const lapDataServiceCollection: Map<SanctioningBodyEnum, Map<OriginType, LapDataService>> = new Map(
  (Object.values(SanctioningBodyEnum) as SanctioningBodyEnum[])
    .filter((x) => typeof x === 'number')
    .map((sanctioningBody) => [
      sanctioningBody,
      new Map(
        (Object.values(OriginType) as OriginType[])
          .filter((x) => typeof x === 'number')
          .map((source: OriginType) => [
            source,
            new LapDataService({
              route: 'stream.lap',
              connectionType: StreamRequestType.MotorsportsStreamApi,
              originType: source,
              sanctioningBody: sanctioningBody,
            }),
          ])
      ),
    ])
);
